import React from 'react';
import {CameraView, useMicrophonePermissions} from 'expo-camera';
import {Platform} from 'react-native';
import * as FileSystem from 'expo-file-system';
import uuid from 'react-native-uuid';
import {VideoCodec} from 'expo-camera/build/legacy/Camera.types';
import {FileTypeApi, PickerAssetExtension} from '../types/Files';
import {useAppDispatch, useAppSelector} from '../store/hooks';
import {
  setFacing,
  setRecording,
  setShootingMode,
} from '../store/slices/camera';
import {
  useFileSelectOptions,
  useNavigateWithAsset,
} from './useFileSelectOptions';
import {setToastText} from '../store/slices/ui';

export function useCameraActions(
  chatId: string,
  camera: React.MutableRefObject<CameraView | undefined>,
) {
  const navigateWithAsset = useNavigateWithAsset(chatId);
  const dispatch = useAppDispatch();
  const [microphonePermission, requestMicrophonePermission] =
    useMicrophonePermissions();
  const facing = useAppSelector(state => state.camera.cameraFacing);
  const cameraMode = useAppSelector(
    state => state.camera.shootingMode,
  );
  const recording = useAppSelector(state => state.camera.recording);
  const {selectImage} = useFileSelectOptions(chatId);

  const makePhoto = async () => {
    try {
      const picture = await camera.current?.takePictureAsync({
        imageType: 'jpg',
      });

      if (picture) {
        if (Platform.OS !== 'web') {
          const info = (await FileSystem.getInfoAsync(picture.uri, {
            size: true,
          })) as FileSystem.FileInfo & {size: number};
          const asset = picture as typeof picture &
            PickerAssetExtension;
          asset.name = uuid.v4().toString();
          asset.fileType = FileTypeApi.JPG;
          asset.uri =
            Platform.OS === 'android'
              ? asset.uri
              : asset.uri.replace('file://', '');
          asset.mimeType = 'image/jpg';
          asset.fileSize = info.size;
          asset.size = info.size;
          navigateWithAsset(asset);
        } else {
          const asset = picture as typeof picture &
            PickerAssetExtension;
          asset.name = uuid.v4().toString();
          asset.fileType = FileTypeApi.JPG;
          asset.mimeType = 'image/jpg';

          console.info('asset', asset);
          navigateWithAsset(asset);
        }
      }
    } catch (err) {
      const errMessage = (err as Error)?.message || err;
      dispatch(setToastText(`photo recording error: ${errMessage}`));
    }
  };

  const toggleCameraFacing = () =>
    dispatch(setFacing(facing === 'back' ? 'front' : 'back'));

  const startVideo = async () => {
    try {
      if (microphonePermission?.granted === false) {
        const res = await requestMicrophonePermission();

        if (res.granted === false) {
          dispatch(setToastText('Microphone permission required'));

          return;
        }
      }
      dispatch(setRecording(true));
      const video = await camera.current?.recordAsync({
        codec: VideoCodec.H264,
      });

      if (video) {
        const info = (await FileSystem.getInfoAsync(video.uri, {
          size: true,
        })) as FileSystem.FileInfo & {size: number};
        const asset = video as typeof video & PickerAssetExtension;
        asset.name = uuid.v4().toString();
        asset.fileType = FileTypeApi.AVI;
        asset.mimeType = 'video/x-msvideo';
        asset.fileSize = info.size;
        asset.size = info.size;
        navigateWithAsset(asset);
      }
    } catch (err) {
      const errMessage = (err as Error)?.message || err;
      dispatch(setToastText(`video recording error: ${errMessage}`));
    }
  };

  const stopRecording = async () => {
    dispatch(setRecording(false));
    camera.current?.stopRecording();
  };

  const switchToPhoto = () => dispatch(setShootingMode('photo'));
  const switchToVideo = () => dispatch(setShootingMode('video'));

  return {
    stopRecording,
    startVideo,
    toggleCameraFacing,
    makePhoto,
    cameraMode,
    facing,
    recording,
    selectImage,
    switchToPhoto,
    switchToVideo,
  };
}
